import { TextField, Box, Button, Typography } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { useAtomValue } from 'jotai';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Document_Type_Enum } from '@kheops/graphql';
import { useInsertCommentMutation } from '../../../mutations/__generated__/insertComment.generated';
import { userAtom } from '../../../state';
import { useUploadFile } from '../../../hooks/useUploadFile';
import FileUtils, { FileParameters } from '../../../common/utils/file.utils';
import CommentAttachmentPreview from './CommentAttachmentPreview';

export interface AddCommentProps {
  orderReferenceId: string;
  orderId: string;
}

interface CommentAttachment {
  file: File;
  fileParams: FileParameters;
}

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const SUPPORTED_FILE_TYPES = ['.doc', '.xlsx', '.xls', '.pdf', '.docx'];
const SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/png'];

export const SUPPORTED_IMAGES_TYPES = ['.jpg', '.jpeg', '.png'];

export const isSupportedImageExtension = (fileExtension: string): boolean => {
  return SUPPORTED_IMAGES_TYPES
    .map((extension) => extension.slice(1).toLowerCase()).includes(fileExtension);
};

export default function AddComment({ orderReferenceId, orderId }: AddCommentProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'comments']);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);
  const user = useAtomValue(userAtom);
  const [insertComment, { data }] = useInsertCommentMutation({ refetchQueries: ['commentsByOrder'] });
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [comment, setComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState<CommentAttachment[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { handleFileChange } = useUploadFile();

  const handlePublishClick = (): void => {
    insertComment({
      variables: {
        comment: {
          content: comment,
          user_id: user.id,
          order_reference_id: orderReferenceId,
          attachments: {
            data: commentAttachments.map(({ fileParams, file }) => ({
              domain: fileParams.domain,
              path: fileParams.path,
              type: Document_Type_Enum.CommentFile,
              order_id: orderId,
              name: file.name,
            })),
          },
        },
      },
    });

    setCommentAttachments([]);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setComment(event.target.value);
  };

  useEffect(() => {
    if (data?.insert_comment) {
      setComment('');
    }
  }, [data]);

  const openFileInput = (): void => {
    fileInputRef.current!.click();
  };

  const openPhotoInput = (): void => {
    photoInputRef.current!.click();
  };

  const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setUploadingAttachments(true);

    try {
      const { files } = event.target;
      if (!files) {
        return;
      }

      setErrorMessage(null);

      await Promise.all(Array.from(files).map(async (file) => {
        const { extension } = FileUtils.parseFilename(file.name);

        if (isSupportedImageExtension(extension) && file.size > MAX_IMAGE_SIZE_BYTES) {
          setErrorMessage(t('comments:picture_is_too_big', { count: MAX_IMAGE_SIZE_BYTES / 1024 / 1024 }));
          return;
        }
        const fileParams = await handleFileChange(file, Document_Type_Enum.CommentFile, user.id);
        setCommentAttachments((prev) => [{ file, fileParams }, ...prev]);
      }));
    } finally {
      // empty the value attribute of the input file so we can re-upload the same file in case of error
      event.target.value = '';
      setUploadingAttachments(false);
    }
  };

  const handleDeleteAttachment = (index: number): void => {
    setCommentAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Box
        onClick={() => {
          commentInputRef.current?.focus();
        }}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 0.5,
          px: 1.5,
          borderRadius: 3,
          backgroundColor: 'surfaceContainerHighest.main',
          cursor: 'text',
          '&:hover': {
            boxShadow: `inset 0 0 0 1px ${theme.palette.outline.main}`,

          },
          '&:focus-within': {
            boxShadow: `inset 0 0 0 3px ${theme.palette.primary.main}`,
          },
        })}
      >
        <TextField
          sx={{
            width: '100%',
            py: 1.5,
          }}
          multiline
          variant="standard"
          placeholder={t('comments:your_comment')}
          value={comment}
          onChange={handleCommentChange}
          autoFocus
          slotProps={{
            input: {
              disableUnderline: true,
            },
          }}
          inputRef={commentInputRef}
        />

        {!!commentAttachments.length && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignSelf: 'flex-start',
              gap: 1,
              overflowX: 'auto',
              width: '100%',
            }}
          >
            {commentAttachments.map((commentAttachment, index) => (
              <Box
                key={commentAttachment.fileParams.path}
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                <Button
                  onClick={() => handleDeleteAttachment(index)}
                  sx={{
                    position: 'absolute',
                    backgroundColor: 'inverseSurface.main',
                    width: 20,
                    height: 20,
                    minHeight: 20,
                    right: 5,
                    top: 5,
                  }}
                >
                  <CloseOutlinedIcon sx={{ width: 12, height: 12, color: 'white' }} />
                </Button>
                <CommentAttachmentPreview
                  fileParams={commentAttachment.fileParams}
                  fileName={commentAttachment.file.name}
                  ispreviewedInCommentInsertion
                />
              </Box>
            ))}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            px: 1,
            py: 0.5,
          }}
        >
          <Box sx={{ gap: 1 }}>
            <Button
              onClick={openFileInput}
              loading={uploadingAttachments}
            >
              {!uploadingAttachments && <AttachFileOutlinedIcon />}
            </Button>
            <Button
              sx={{
                display: {
                  xs: 'inline-flex',
                  lg: 'none',
                },
              }}
              onClick={openPhotoInput}
              loading={uploadingAttachments}
            >
              {!uploadingAttachments && <PhotoCameraOutlinedIcon />}
            </Button>
          </Box>
          <Button
            onClick={handlePublishClick}
            disabled={!comment.length && !commentAttachments.length}
            variant="contained"
          >
            <SendOutlinedIcon />
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileInputChange}
            style={{ display: 'none' }}
            multiple
            accept={[...SUPPORTED_FILE_TYPES, ...SUPPORTED_IMAGES_TYPES, ...SUPPORTED_MIME_TYPES].join(',')}
          />
          <input
            type="file"
            ref={photoInputRef}
            onChange={onFileInputChange}
            style={{ display: 'none' }}
            multiple
            accept={[...SUPPORTED_IMAGES_TYPES, ...SUPPORTED_MIME_TYPES].join(',')}
            capture="environment"
          />
        </Box>
      </Box>
      {errorMessage && (
        <Typography
          variant="labelSmall"
          sx={{
            color: 'error.main',
            mt: 1,
          }}
        >
          {errorMessage}
        </Typography>
      )}
    </>
  );
}
